// ═══════════════════════════════════════════════════════════════════════════
// cotaDeLectura — toda lectura de colección declara hasta dónde lee
// ═══════════════════════════════════════════════════════════════════════════
//
// EL PROBLEMA (ítem 18 · ADR 016 · `A1`/`ESC-03`)
// ──────────────────────────────────────────────
// Un dashboard que consulta una colección sin cota lee **todo lo que haya**.
// Con los volúmenes de hoy no se nota; con los de un seller grande, la misma
// pantalla se trae decenas de miles de documentos, y lo hace en el navegador
// del cliente. No falla: se pone lenta, factura lecturas y un día no vuelve.
// Es el modo de falla que degrada en silencio, que es el que `ADR 066` manda
// automatizar primero.
//
// ⚠️ ADR 016 dio esto por "enforced" y **el candado nunca existió**. Medido al
// escribir esto: de 48 call sites de `useDashboardData`, **32 no declaraban
// ninguna cota**.
//
// LA REGLA, EN UNA LÍNEA
// ──────────────────────
// Toda lectura declara su cota: `cap` (umbral de alarma — no recorta),
// `limit` (recorte silencioso, para listas cortas y queries ordenadas), o el
// opt-out EXPLÍCITO con su motivo.
//
// POR QUÉ EL OPT-OUT VIVE EN EL CALL SITE Y NO EN UNA ALLOWLIST
// ────────────────────────────────────────────────────────────
// Una lista de excepciones en otro archivo se llena y no se vacía nunca: nadie
// la revisa, y el motivo se despega del código que lo justifica. Acá el motivo
// se lee al lado de la query que exime, y se mueve o se borra con ella. Es el
// molde de `scripts/check-mail-empresaid.cjs` ("OPT-OUT: no hay").
//
// POR QUÉ TIRA EN DEV Y NO EN PROD
// ────────────────────────────────
// En dev, una lectura sin cota es un error de programación y tiene que doler
// ya, con el nombre de la colección adentro del mensaje. En producción, tirar
// convertiría una lectura cara en una **pantalla rota**: cambiaría un problema
// de costo por uno de disponibilidad, que es peor.
//
// ⭐⭐ Y EN PROD NO RECORTA — LA DECISIÓN QUE MÁS IMPORTA DE ESTE ARCHIVO
// ────────────────────────────────────────────────────────────────────
// La primera versión degradaba a un cap por defecto: recortaba y avisaba. Se
// descartó (Mariano, 16-ago-2026) con un argumento que no tiene vuelta:
// **el recorte no elige, y por lo tanto no sabemos qué deja afuera.** Donde la
// query no declara un `orderBy`, Firestore corta por nombre de documento — un
// orden que no significa nada. Textual: *"no tenemos forma de saber, quizás
// estamos dejando fuera publicaciones con mucho tráfico y ventas"*. Y en las
// pantallas que suman plata, un total que cubre "una parte cualquiera" es peor
// que uno que tarda: el usuario decide sobre un número que cree completo.
//
// Así que el camino de producción es el CARO y no el silencioso: se lee sin
// recortar y se grita. `logger.error` —y no `warn`— porque es el único nivel
// que persiste a `clientLogs` (`logger.js`): un aviso que muere en la consola
// del navegador del cliente no lo lee nadie, que es lo mismo que no avisar.
//
// El bound de verdad no vive acá: vive en DEV (esto tira) y en el checker de
// CI, que mira el árbol entero. Este camino es el "esto no debería pasar
// nunca", y para ese camino la regla del repo es fallar VISIBLE, no barato.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Marca que el hook devuelve cuando una lectura llegó a producción sin
 * declarar su cota. NO es un cap: la lectura sale igual, sin recortar (ver
 * el encabezado). Existe para que el hook sepa que tiene que gritar.
 */
export const SIN_DECLARAR_EN_PROD = 'sin-declarar-en-prod'

/**
 * Opt-out explícito. Se pasa como `cap` y **exige `motivoSinCap`**.
 *
 *   useDashboardData({
 *     collectionName: 'categorias',
 *     cap: SIN_CAP_DECLARADO,
 *     motivoSinCap: 'catálogo de referencia acotado por diseño (~200 docs)',
 *   })
 *
 * Es un Symbol y no un `false`/`0` a propósito: no se puede escribir por
 * accidente, no sobrevive a un `JSON.parse`, y en el AST del checker se ve
 * como lo que es — una decisión tomada, no un valor omitido.
 */
export const SIN_CAP_DECLARADO = Symbol('sin-cap-declarado')

// ═══════════════════════════════════════════════════════════════════════════
// Umbrales con procedencia
// ───────────────────────────────────────────────────────────────────────────
// ⚠️ Esto NO es una tabla "colección → cap", y la distinción importa porque esa
// tabla se evaluó y se descartó: aplanaría diferencias reales (de las cuatro
// lecturas de `publicaciones` que ya tenían cap, una es una query mucho más
// ancha que las otras, y su número es residuo de otra época). Un umbral es
// configuración de alarma de UN consumidor, no una propiedad de la colección.
//
// Lo que sí se comparte es el MOTIVO: cuando varias lecturas quieren que les
// avisen por la misma razón y con el mismo origen citable, el número vive una
// sola vez. El que no comparta el motivo escribe el suyo en el call site.
//
// Y algo que hace barato equivocarse: desde el 16-ago un cap **no recorta**.
// Un umbral flojo cuesta una alarma de más o de menos, jamás un dato faltante.
// Por eso se prefiere un número con origen citable sobre esperar una medición.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * El volumen que YA hizo doler esta plataforma: `ADR 016` nace de *"una cuenta
 * de ~20k publicaciones puso lento todo"*. No es un límite técnico — es el
 * tamaño en el que la experiencia se degradó una vez, medido en producción.
 */
export const UMBRAL_VOLUMEN_QUE_YA_DOLIO = 20000

/**
 * Métricas, ítems y **ventanas comparativas** de campañas. Coincide con el techo que el
 * runner del rollup ya eligió para la query equivalente
 * (`functions/dashboardRollups/pulsoRollupCore.js`, `CAP_CAMPANAS_METRICAS`).
 * ⚠️ Los dos números coinciden y **no están atados**: allá es techo de memoria
 * del proceso, acá es umbral de alarma de una pantalla. Ver el lineage escrito
 * en ese archivo antes de mover cualquiera de los dos.
 *
 * ⚠️ Las ventanas (`campanasMetricasVentanas`) entran acá y NO tienen umbral
 * propio. Al escribir esto le puse uno de 5000 citando `CAP_ORDENES_VENTANA`
 * del servidor, y **esa procedencia era falsa**: ese cap es de las ventanas de
 * ÓRDENES, que es otra colección. El servidor no eligió ningún techo para las
 * ventanas de campañas — `pulsoInsumos.js` las lee sin cap. El único número
 * que sí existía en el árbol para esta colección es el de `CAP_PER_AD`, que es
 * este mismo. Queda anotado porque un motivo falso al lado de una query es
 * peor que no tener motivo: el próximo lo da por verificado.
 */
export const UMBRAL_METRICAS_DE_CAMPANA = 25000

/**
 * Vínculos publicación↔producto. Es el único umbral que ya estaba elegido en
 * el árbol antes de esta regla (`TraficoConversionDashboard`), y su magnitud
 * tiene sentido: hay varios vínculos por publicación, así que el techo va por
 * encima del de publicaciones, no al mismo nivel.
 */
export const UMBRAL_VINCULOS = 40000

/**
 * Campañas de una cuenta —de publicidad y de promociones, que viven en la
 * misma colección con distinto `tipo`. ⚠️ **Sin medición**: no hay un número
 * previo en el árbol ni el servidor eligió uno. Es un techo deliberadamente
 * holgado —una cuenta con miles de campañas ya sería anómala— y se corrige con
 * el primer aviso real. Se prefiere esto a dejar la lectura sin declarar:
 * como el cap NO recorta, equivocarle el número cuesta una alarma de más o de
 * menos, nunca un dato faltante.
 */
export const UMBRAL_CAMPANAS = 5000

/**
 * Catálogo de `productos` de una cuenta. ⚠️ **Sin medición propia**: el techo
 * está DERIVADO del de publicaciones (`UMBRAL_VOLUMEN_QUE_YA_DOLIO`), y por
 * construcción `productos` va por debajo de él —un producto puede tener varias
 * publicaciones—, así que este umbral es **probablemente alto** y puede no
 * sonar en el tenant que ya dolió. Se deja igual, con el defecto escrito, por
 * dos razones: el cap **no recorta** (errarle cuesta una alarma de más o de
 * menos, jamás un dato faltante) y un número con origen citable es preferible
 * a dejar la lectura sin declarar. Se corrige con el primer aviso real.
 *
 * ⚠️ Y NO se reusa el nombre `UMBRAL_VOLUMEN_QUE_YA_DOLIO` en el call site a
 * propósito: ese nombre afirma una medición de OTRA colección, y en el punto
 * de uso el nombre es lo único que se lee.
 *
 * Tampoco lleva el opt-out de catálogo: `productos` **no** lo carga sólo una
 * persona —`ImportarDesdeMlModal` los trae del canal—, así que crece con el
 * volumen de la cuenta y un umbral acá sí tiene sentido.
 */
export const UMBRAL_CATALOGO_DE_PRODUCTOS = 20000

/**
 * Motivo de opt-out de los catálogos del tenant.
 *
 * ⚠️ Que el motivo viva EN EL CALL SITE es la decisión de diseño de este
 * archivo, y sigue en pie: quien lee la query lee por qué está exenta, sin
 * saltar a otro lado. Lo que NO exige esa decisión es que el literal esté
 * escrito once veces — once copias del mismo texto son once lugares donde
 * puede divergir, y el nombre de la constante dice lo mismo en el punto de uso.
 * Lo marcó el adversarial sobre el PR #918 (bitácora, 17-ago) y tenía razón.
 *
 * Aplica a colecciones que **una persona carga a mano**: marcas, proveedores,
 * categorías, sourcing. No crecen con el volumen de la cuenta, así que un
 * umbral acá no sonaría nunca — y un aviso que no suena nunca es indistinguible
 * de uno que no existe.
 *
 * ⚠️ "CATEGORÍAS" ACÁ ES `categorias`, EL MAESTRO DEL SELLER — NO `categoriasCanal`.
 * Desde el 9-sep-2026 hay dos colecciones con nombres parecidos y sólo una
 * califica para este opt-out:
 *   · `categorias` — el maestro de rubros del tenant, con `ivaPct` y
 *     `rentabilidadMinima`, escrito por el cliente desde la app. Es el que este
 *     motivo exime, y sigue siendo correcto.
 *   · `categoriasCanal` — el árbol de rubros que el seller definió en su tienda
 *     de Tienda Nube, escrito por un **sync**. NO califica, y la razón es
 *     literal: la premisa de este motivo es "lo carga una persona, no un sync".
 *     Crece con lo que haya en la tienda del canal, sin que nadie de este lado
 *     lo cargue ni lo pueda acotar — así que su lectura declara un umbral
 *     NUMÉRICO, no este opt-out. Un guard lo blinda
 *     (`optOutDeCatalogo.guard.test.js`), porque el parecido de los nombres
 *     hace de este el copy-paste más fácil de todo el archivo.
 */
export const MOTIVO_CATALOGO_DEL_TENANT =
  'catálogo del tenant: lo carga una persona, no un sync — no crece con el volumen de la cuenta'

/**
 * El directorio de usuarios de TODA la plataforma. Lo comparten las lecturas que
 * necesitan el conjunto completo —no una tajada— porque con un recorte el
 * resultado no queda corto: queda MAL. Dos ejemplos del árbol: el organigrama
 * global re-parenta como raíz al subordinado cuyo jefe quedó afuera del set
 * (`src/modules/plataforma/components/OrganigramaTree.jsx:86` y `:96`), y la
 * administración de usuarios pinta "N usuarios en el sistema" con el largo de lo
 * que trajo la lectura (el subtítulo del header de `src/modules/gestion/pages/UsuariosPage.jsx`).
 *
 * LOS CINCO CONSUMIDORES, al cerrarse el report du3xh8vn8Gmd8fmmf5aB:
 *   · `src/core/components/CrearAccionableModal.jsx`  — selector de asignado
 *   · `src/modules/gestion/pages/SLAsPage.jsx`        — destinatarios de alerta
 *   · `src/modules/gestion/pages/UsuariosPage.jsx`    — rama staff del ABM
 *   · `src/modules/gestion/pages/EmpresaDetailPage.jsx` — equipo y organigrama
 *   · `src/modules/plataforma/pages/OrganigramaPage.jsx` — organigrama global
 *
 * ⚠️ Y el SEXTO call site de ese reporte NO comparte este umbral, a propósito:
 * `src/core/pages/PlataformaDashboard.jsx` sólo necesitaba CUÁNTOS, así que no
 * lee documentos — usa `getCountFromServer`. Un umbral de alarma sobre una
 * agregación no tendría sentido (en una agregación el `limit` recorta el
 * RESULTADO, o sea que el número mentiría). Se dice acá porque "seis call sites
 * del reporte, cinco consumidores" es justo la clase de resta que el próximo
 * lee como un olvido.
 *
 * Comparten el umbral porque comparten el MOTIVO y el origen citable, que es la
 * excepción que este archivo ya declara arriba — no es la tabla central
 * "colección → cap" que el ADR del 16-ago descartó.
 *
 * ⚠️ **SIN MEDICIÓN, y acá medir era barato.** Nadie contó los usuarios de la
 * plataforma: desde el sandbox de la guardia no hay forma de consultar
 * Firestore, así que el número es un techo holgado elegido por orden de
 * magnitud y no por medición. Se deja igual, con el defecto escrito, por el
 * mismo argumento que `UMBRAL_CAMPANAS`: el cap **no recorta**, así que errarle
 * cuesta una alarma de más o de menos, jamás un dato faltante. Y se corrige con
 * el primer aviso real — o antes, si alguien corre el conteo.
 */
export const UMBRAL_DIRECTORIO_DE_LA_PLATAFORMA = 2000

/**
 * ¿La lectura trajo más que el umbral declarado?
 *
 * ⚠️ El guard de tipo no es defensivo por las dudas: `cap: SIN_CAP_DECLARADO`
 * es un **Symbol**, y un Symbol es truthy — `recibidos > Symbol` tiraría
 * `TypeError`. En el camino normal el Symbol no llega hasta acá porque
 * `resolverCota` lo traduce a `cap: null` antes (los consumidores usan los
 * valores RESUELTOS, nunca los props), pero ésta es una función exportada y el
 * guard es su propia red.
 *
 * Vivía en `useDashboardData.js` hasta el 1-oct-2026. Se mudó acá porque la
 * regla y su única implementación estaban en archivos distintos, y el hook no
 * puede ser la fuente de algo que una lectura IMPERATIVA también necesita — el
 * lector imperativo es la tajada siguiente de este mismo trabajo (report
 * du3xh8vn8Gmd8fmmf5aB) y va a importarla de acá.
 */
export function superaUmbral({ recibidos, cap }) {
  if (typeof cap !== 'number' || !Number.isFinite(cap) || cap <= 0) return false
  return recibidos > cap
}

/**
 * Los dos gritos de una lectura, y la FORMA de su contexto — una sola vez.
 *
 * Las consultas a `clientLogs` se apoyan en estos strings literales, así que
 * dos copias son dos lugares donde el mensaje puede divergir y dejar media
 * consulta ciega. Y la forma del ctx importa tanto como el texto:
 *
 * ⚠️ `logger.error` se llama SIEMPRE con **TRES** argumentos
 * —`error(mensaje, errObj, ctx)`—. Con dos, el contexto cae en la posición de
 * `errObj`, `ctx` queda `undefined` y **`addDoc` rechaza el documento entero**
 * (Firestore no acepta `undefined` y este proyecto no tiene
 * `ignoreUndefinedProperties`). Y como `addDoc` tira SÍNCRONO, el `.catch` del
 * logger no se engancha y se pierde el lote completo de hasta 20 entradas,
 * incluidos los errores ajenos que viajaban ahí. Esto ya se pagó una vez; no se
 * "simplifica".
 */
export const MENSAJE_UMBRAL_EXCEDIDO = 'lectura por encima del umbral declarado'
export const MENSAJE_SIN_COTA = 'lectura sin cota declarada'

/**
 * ⭐⭐ LA OTRA MITAD DE LA REGLA: el `limit` que se ALCANZÓ.
 *
 * Este archivo declara arriba DOS instrumentos —`cap` (umbral de alarma, no
 * recorta) y `limit` (recorte silencioso, para listas cortas y queries
 * ordenadas)— y hasta hoy sólo tenía helper para el primero. El segundo no es
 * un detalle: una lectura con `limit` **ya recortó** cuando vuelve, así que el
 * número que sale de ella no queda "corto y avisado", queda **mal y callado**.
 *
 * ⚠️ Y NO SE PUEDE REUSAR `superaUmbral` PARA ESTO, que es la trampa que esta
 * función existe para cerrar: `superaUmbral` compara con `>` —correcto para un
 * `cap`, que no recorta y por lo tanto SÍ se puede exceder—. Un `limit` no se
 * puede exceder nunca: `recibidos` topea exactamente en `limite`, así que
 * `recibidos > limite` es **siempre false** y el candado no dispararía jamás.
 * Un candado que no se activa es peor que ninguno: da la sensación de estar.
 * Por eso son dos funciones y no un flag de una, y por eso ésta compara con
 * `>=`.
 *
 * El falso positivo del `>=` —un día que trae exactamente `limite` documentos
 * sin estar recortado— es DELIBERADO y es la dirección segura: una página
 * llena es indistinguible de una recortada, y errar hacia "esto es un mínimo"
 * cuesta un aviso de más, jamás un número que miente. No se "arregla" a `>`.
 *
 * @param {object} args
 * @param {number} args.recibidos — cuántos documentos volvieron.
 * @param {number|null} [args.limite] — el `limit` declarado en la query.
 * @returns {boolean}
 */
export function limiteAlcanzado({ recibidos, limite }) {
  if (typeof limite !== 'number' || !Number.isFinite(limite) || limite <= 0) return false
  if (typeof recibidos !== 'number' || !Number.isFinite(recibidos)) return false
  return recibidos >= limite
}

/**
 * El grito de una lectura RECORTADA, con su literal propio.
 *
 * ⚠️ No comparte el string con `MENSAJE_UMBRAL_EXCEDIDO` a propósito: las
 * consultas a `clientLogs` se apoyan en estos literales, y las dos condiciones
 * no son la misma cosa —una avisa que una lectura creció por encima de lo
 * esperado sin perder nada, la otra avisa que ya se perdieron documentos—.
 * Un literal compartido dejaría media consulta respondiendo la pregunta
 * equivocada. Rige para él la misma forma de ctx: `logger.error` SIEMPRE con
 * TRES argumentos (ver el bloque de arriba — con dos se pierde el lote entero).
 */
export const MENSAJE_LIMITE_ALCANZADO = 'lectura recortada por su limit declarado'

/**
 * Un motivo vacío, o de dos palabras, no es un motivo. Exportado para la paridad
 * con `// cota-sin-orden:` en `scripts/check-cota-de-lectura.cjs`.
 */
export const MOTIVO_MINIMO = 15

export class CotaNoDeclaradaError extends Error {
  constructor(mensaje) {
    super(mensaje)
    this.name = 'CotaNoDeclaradaError'
  }
}

const esEnteroPositivo = (n) => Number.isInteger(n) && n > 0

/**
 * Resuelve la cota de una lectura de colección.
 *
 * @param {object} args
 * @param {string} args.collectionName — sólo para el mensaje de error: sin el
 *        nombre adentro, "falta el cap" no le dice a nadie dónde mirar.
 * @param {number|symbol|null} [args.cap]
 * @param {number|null} [args.limit]
 * @param {string} [args.motivoSinCap] — obligatorio si `cap === SIN_CAP_DECLARADO`.
 * @param {string|null} [args.orderByField] — obligatorio si se declara `limit`.
 * @param {boolean} [args.esDev=false] — en dev tira; en prod degrada.
 * @returns {{ cap: number|null, limit: number|null, porDefecto: boolean, sinCota: boolean }}
 * @throws {CotaNoDeclaradaError} sólo si `esDev`.
 */
export function resolverCota({ collectionName, cap = null, limit = null, motivoSinCap = '', orderByField = null, esDev = false } = {}) {
  // 1. Opt-out explícito — vale sólo con su motivo.
  if (cap === SIN_CAP_DECLARADO) {
    if (String(motivoSinCap || '').trim().length < MOTIVO_MINIMO) {
      // Un opt-out sin motivo es un opt-out sin revisar: se trata como si no
      // estuviera declarado. Si no, `SIN_CAP_DECLARADO` sería un `// eslint-disable`
      // sin explicación y la regla se vaciaría sola en tres meses.
      return sinDeclarar(collectionName, esDev,
        `\`cap: SIN_CAP_DECLARADO\` sin \`motivoSinCap\` (mínimo ${MOTIVO_MINIMO} caracteres)`)
    }
    return { cap: null, limit: null, porDefecto: false, sinCota: true }
  }

  // 2. Cota fail-loud.
  if (esEnteroPositivo(cap)) return { cap, limit: null, porDefecto: false, sinCota: false }

  // 3. Cota silenciosa. Vale como declaración —el que escribe `limit: 50` sabe
  //    que está pidiendo 50— **pero sólo si la query declara un orden**.
  //
  //    ⚠️ Sin `orderBy`, Firestore devuelve los primeros N por nombre de
  //    documento: el recorte no elige, y el que lo escribió cree que sí. Es
  //    exactamente el defecto que el 16-ago se le sacó a `cap`, y hasta hoy
  //    esta rama lo dejaba entrar por la otra puerta. Si querés las N más
  //    recientes, decilo: `limit: N` + `orderByField`.
  if (esEnteroPositivo(limit)) {
    if (!orderByField) {
      return sinDeclarar(collectionName, esDev,
        '`limit` sin `orderByField` — un recorte sin orden se queda con documentos al azar')
    }
    return { cap: null, limit, porDefecto: false, sinCota: false }
  }

  // 4. Nada declarado.
  return sinDeclarar(collectionName, esDev, 'no declara `cap` ni `limit`')
}

function sinDeclarar(collectionName, esDev, detalle) {
  if (esDev) {
    throw new CotaNoDeclaradaError(
      `Lectura sin cota en "${collectionName}": ${detalle}.\n` +
      `Toda lectura de colección declara hasta dónde lee (ítem 18 / ADR 016). Elegí una:\n` +
      `  · cap: <n>            — umbral de alarma: NO recorta, avisa con \`capExcedido\` (lo habitual)\n` +
      `  · limit: <n> + orderByField — recorte deliberado: las N primeras SEGÚN ESE ORDEN\n` +
      `  · cap: SIN_CAP_DECLARADO, motivoSinCap: '<por qué esta colección no puede crecer>'\n` +
      `En producción esto NO tira ni recorta: lee todo y lo reporta como error.`,
    )
  }
  // Sin recortar, a propósito: recortar acá elegiría qué dejar afuera por
  // nombre de documento, o sea sin criterio, y en silencio.
  return { cap: null, limit: null, porDefecto: true, sinCota: true, motivoDeAlerta: SIN_DECLARAR_EN_PROD }
}
